import * as React from 'react';
import { useState, useEffect, SyntheticEvent } from 'react';

import { DropdownOption, utils } from 'kb-shared';
import Button from '../../../components/Button/NewButton';
import TextInput from '../../../components/TextInput/NewTextInput';
import SelectInput, { SelectWithOptions } from '../../../components/SelectInput';
import {
  InputContainer,
  VMarginWrapper,
  ButtonWrapper,
  TwoFieldWrapper,
  InputWrapperMargin,
  DisclaimerText,
  GenderInputWrapper
} from '../../../components/SignUpFormShared';

import useCreatePatient from './hooks/use-create-patient';
import RecognizePatientResultCode from './types/recognize-patient-result-code';
import NewPatientData from './types/new-patient-data';
import styled from 'styled-components';
import TermsOfUse from 'components/TermsOfUse';

const ButtonWrapperPrivacy = styled(ButtonWrapper)`
  padding-bottom: 8px;
`;

const {
  GENDER_INPUT_DEFAULTS,
  PRONOUN_INPUT_DEFAULTS,
  isEmailValid,
  isInputValid,
  isPhoneNumberValid,
  isDOBValid,
  isDateValid
} = utils;

const initialPatientData: NewPatientData = {
  email: '',
  firstName: '',
  lastName: '',
  birthday: '',
  gender: GENDER_INPUT_DEFAULTS[1].value, // female
  phone: '',
  pronoun: '',
  referralSource: ''
};

const initialDropdownData = {
  genderOption: GENDER_INPUT_DEFAULTS[1], // { label: 'female', value: 'female' }
  pronounOption: { label: '', value: '' }
};

const STEPS = {
  editingForm: 'editingForm',
  submittingForm: 'submittingForm'
};

export default function CreatePatientForm(props: {
  idtoken: string;
  accessToken: string;
  onPatientSuccessfullyCreated: any;
}) {
  const [step, setStep] = useState(STEPS.editingForm);
  const [error, setError] = useState('');
  const { idtoken, accessToken, onPatientSuccessfullyCreated } = props;
  const [createPatientResponse, createPatient] = useCreatePatient();

  const [patientData, setPatientData] = useState(initialPatientData);
  const [drowpdownData, setDropdownData] = useState(initialDropdownData);

  const updatePatientData = (field: string, event: any) => {
    const value = event.currentTarget.value;
    setPatientData({ ...patientData, [field]: value });
  };

  const updateGender = (gender: { label: string; value: string }) => {
    setDropdownData({ ...drowpdownData, genderOption: gender });
    setPatientData({ ...patientData, gender: gender.value });
  };

  const updatePronoun = (pronoun: { label: string; value: string }) => {
    setDropdownData({ ...drowpdownData, pronounOption: pronoun });
    setPatientData({ ...patientData, pronoun: pronoun.value });
  };

  const updateReferralSource = ({ value }: DropdownOption) => {
    setPatientData({ ...patientData, referralSource: value });
  };

  useEffect(() => {
    if (step == STEPS.submittingForm) {
      const [mm, dd, yyyy] = patientData.birthday.split('/');
      const formattedPatientData = { ...patientData, birthday: `${yyyy}-${mm}-${dd}` };
      createPatient(idtoken, accessToken, formattedPatientData);
    }
  }, [step]);

  useEffect(() => {
    if (!(step == STEPS.submittingForm && createPatientResponse.finished)) return;

    if (createPatientResponse.resultCode == RecognizePatientResultCode.PatientCreated) {
      onPatientSuccessfullyCreated();
    } else if (createPatientResponse.resultCode == RecognizePatientResultCode.PatientRecognized) {
      // well, this scenario should never happen, but just in case..
      onPatientSuccessfullyCreated();
    } else {
      setStep(STEPS.editingForm);
      setError((createPatientResponse.errors as []).join('. '));
    }
  }, [createPatientResponse]);

  const locallyValidateForm = () => {
    const validationErrors = [];
    if (!isEmailValid(patientData.email)) validationErrors.push('Please enter a valid email');
    if (!isInputValid(patientData.firstName))
      validationErrors.push('Please enter a valid first name');
    if (!isInputValid(patientData.lastName))
      validationErrors.push('Please enter a valid last name');
    if (!isDOBValid(patientData.birthday))
      validationErrors.push('Patients must be 18 years or older to schedule a consult');
    if (!isDateValid(patientData.birthday)) validationErrors.push('Please enter a valid date');
    if (!isInputValid(patientData.gender)) validationErrors.push('You must select a gender');
    if (!isPhoneNumberValid(patientData.phone))
      validationErrors.push('You must provide a phone number');

    if (!isInputValid(patientData.referralSource))
      validationErrors.push('Please let us know how you heard about us');

    setError(validationErrors.join('. '));
    return validationErrors.length == 0;
  };

  const submitForm = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (locallyValidateForm()) setStep(STEPS.submittingForm);
  };

  return (
    <form onSubmit={event => submitForm(event)} autoComplete="on">
      <div style={{ maxWidth: '400px' }}>
        You are successfully logged in via My Premise Health. Before you can proceed to Kindbody
        Patient portal, please fill in your personal details.
      </div>
      <div style={{ maxWidth: '400px', marginTop: '15px' }}>{error}</div>
      <InputContainer>
        <VMarginWrapper>
          <TextInput
            name={'email'}
            label={'EMAIL ADDRESS'}
            value={patientData.email}
            onChange={event => updatePatientData('email', event)}
            autoComplete="email"
            spellCheck="false"
            placeholder={'janedoe@email.com'}
            inputMode="email"
          />
        </VMarginWrapper>
        <VMarginWrapper>
          <TwoFieldWrapper>
            <InputWrapperMargin>
              <TextInput
                name={'first-name'}
                label={'FIRST NAME'}
                value={patientData.firstName}
                onChange={event => updatePatientData('firstName', event)}
                autoComplete="given-name"
                spellCheck="false"
                placeholder={'First name'}
              />
            </InputWrapperMargin>
            <InputWrapperMargin>
              <TextInput
                name={'last-name'}
                label={'LAST NAME'}
                value={patientData.lastName}
                onChange={event => updatePatientData('lastName', event)}
                autoComplete="family-name"
                spellCheck="false"
                placeholder={'Last name'}
              />
            </InputWrapperMargin>
          </TwoFieldWrapper>
          <DisclaimerText>
            Be sure to enter your full legal name for your medical record.
          </DisclaimerText>
        </VMarginWrapper>
        <VMarginWrapper>
          <TwoFieldWrapper>
            <InputWrapperMargin>
              <TextInput
                inputType={'phone'}
                name={'phone'}
                label={'PHONE NUMBER'}
                autoComplete="tel"
                value={patientData.phone}
                onChange={event => updatePatientData('phone', event)}
              />
            </InputWrapperMargin>
            <InputWrapperMargin>
              <TextInput
                inputType={'date'}
                name={'birthdate'}
                label={'BIRTHDATE'}
                autoComplete="bday"
                value={patientData.birthday}
                onChange={event => updatePatientData('birthday', event)}
              />
            </InputWrapperMargin>
          </TwoFieldWrapper>
        </VMarginWrapper>
        <VMarginWrapper>
          <TwoFieldWrapper>
            <GenderInputWrapper>
              <SelectInput
                label="SEX AT BIRTH"
                extractTitle={item => (item ? item.label : '')}
                placeholder={'Select'}
                selectedOption={drowpdownData.genderOption}
                onSelect={value => updateGender(value)}
                options={GENDER_INPUT_DEFAULTS}
              />
            </GenderInputWrapper>
            <InputWrapperMargin>
              <SelectInput
                label="PRONOUN"
                extractTitle={item => (item ? item.label : '')}
                placeholder={'Select'}
                selectedOption={drowpdownData.pronounOption}
                onSelect={value => updatePronoun(value)}
                options={PRONOUN_INPUT_DEFAULTS}
              />
            </InputWrapperMargin>
          </TwoFieldWrapper>
        </VMarginWrapper>

        <VMarginWrapper>
          <SelectWithOptions
            label="HOW DID YOU HEAR ABOUT US?"
            value={patientData.referralSource}
            placeholder={'Select one option'}
            lookup_key="disney_patient_referral_source"
            onSelect={updateReferralSource}
          />
        </VMarginWrapper>

        <ButtonWrapperPrivacy>
          <Button text={'SIGN UP'} type="submit" loading={step == STEPS.submittingForm} />
        </ButtonWrapperPrivacy>
        <TermsOfUse />
      </InputContainer>
    </form>
  );
}
